"use client";

import React, { useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tracify/ui/dialog";
import { Button } from "@tracify/ui/button";
import LinkWithPersistentQuery from "@components/LinkWithQuery/LinkWithQuery";
import { useConnectedAdAccounts } from "@lib/api-hooks/useAdAccounts/useConnectedAdAccounts";
import { useTrackingParameterErrors } from "@lib/api-hooks/useTrackingParameterErrors";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { FeatureFlagUtils, useFeatureFlags } from "@lib/hooks/useFeatureFlags";

export const TRACKING_ERROR_MODAL_STORAGE_KEY_PREFIX =
  "tracify:tracking-parameter-errors-modal:";
const DISMISSAL_DURATION_MS = 24 * 60 * 60 * 1000;
const TRACKING_SETUP_VIDEO_URL =
  "https://tracify.learningsuite.io/student/course/setup/rqfyD6Hg/t/xBR9SGsX";

type TrackingParameterErrorModalProps = {
  suppressed?: boolean;
};

const TrackingParameterErrorModal = ({
  suppressed = false,
}: TrackingParameterErrorModalProps) => {
  const pathname = usePathname();
  const { selectedOrganisationData } = useSelectedStores();
  const organisationId = selectedOrganisationData?.id;
  const { flags } = useFeatureFlags();

  const [currentTime] = useState(() => Date.now());
  const visibleSources = useMemo(
    () => FeatureFlagUtils.getVisibleTrackingParameterManagers(flags),
    [flags]
  );
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!organisationId) return;
    const raw = localStorage.getItem(
      `${TRACKING_ERROR_MODAL_STORAGE_KEY_PREFIX}${organisationId}`
    );
    if (!raw) {
      setDismissedAt(null);
      return;
    }
    try {
      setDismissedAt(JSON.parse(raw).dismissedAt ?? null);
    } catch {
      setDismissedAt(null);
    }
  }, [organisationId]);

  const { data: connectedAdAccounts } = useConnectedAdAccounts(
    { organisationId: organisationId ?? "" },
    { enabled: !!organisationId }
  );

  const allAccounts = useMemo(
    () =>
      visibleSources.flatMap((source) =>
        (connectedAdAccounts?.accounts[source] ?? []).map((acc) => ({
          account_id: acc.account_id,
        }))
      ),
    [connectedAdAccounts, visibleSources]
  );

  const { data: trackingErrorsData, isFetched } = useTrackingParameterErrors({
    by_accounts: allAccounts,
    limit: 1,
    enabled:
      FeatureFlagUtils.isTrackingParameterErrorsEnabled(flags) &&
      FeatureFlagUtils.isTrackingParameterErrorsModalEnabled(flags) &&
      allAccounts.length > 0,
  });

  const errorCount = trackingErrorsData?.totalCount ?? 0;

  const isDismissed =
    dismissedAt !== null && currentTime - dismissedAt < DISMISSAL_DURATION_MS;

  const isOnExcludedPage = [
    "/settings/tracking-parameter-errors",
    "/settings/account-health",
  ].includes(pathname ?? "");

  const isOpen =
    !suppressed &&
    !!organisationId &&
    FeatureFlagUtils.isTrackingParameterErrorsEnabled(flags) &&
    FeatureFlagUtils.isTrackingParameterErrorsModalEnabled(flags) &&
    isFetched &&
    errorCount > 0 &&
    !isDismissed &&
    !isOnExcludedPage;

  const handleDismiss = () => {
    if (!organisationId) return;
    const now = Date.now();
    setDismissedAt(now);
    localStorage.setItem(
      `${TRACKING_ERROR_MODAL_STORAGE_KEY_PREFIX}${organisationId}`,
      JSON.stringify({ dismissedAt: now })
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleDismiss();
      }}
    >
      <DialogContent
        // Keep the content above its elevated backdrop as well as dashboard panels.
        overlayClassName="!z-[100000]"
        className="max-w-md"
        style={{ zIndex: 100001 }}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-red-700 dark:text-red-200">
            Tracking Parameter Errors Detected
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          We detected <strong>{errorCount}</strong> ad
          {errorCount === 1 ? "" : "s"} with URL parameter errors. It takes just
          a minute to{" "}
          <LinkWithPersistentQuery
            href="/settings/tracking-parameter-errors"
            onClick={handleDismiss}
            className="underline font-medium text-foreground"
          >
            fix them
          </LinkWithPersistentQuery>
          . Your reports are not accurate and will not work properly until these
          errors are addressed.
        </p>
        <DialogFooter>
          <Button variant="outline" asChild>
            <a href={TRACKING_SETUP_VIDEO_URL} target="_blank" rel="noreferrer">
              Watch setup videos
            </a>
          </Button>
          <Button variant="primary" asChild>
            <LinkWithPersistentQuery
              href="/settings/tracking-parameter-errors"
              onClick={handleDismiss}
            >
              Fix errors
            </LinkWithPersistentQuery>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TrackingParameterErrorModal;
